import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DollarSign, Download, ArrowUpCircle, ToggleLeft, ToggleRight, Trash2, Filter, Receipt, Eye, Calendar, MoreVertical, Mail, Snowflake, Users } from 'lucide-react';
import { MembersMembership, MembershipPlan } from '../../types/api';
import { membersMembershipAPI, membershipPlanAPI, paymentReceiptAPI, enquiryAPI, membershipFreezeAPI } from '../../services/api';
import SearchBar from '../common/SearchBar';
import Pagination from '../common/Pagination';
import { ToastContainer } from '../common/Toast';
import { useToast } from '../../hooks/useToast';

const Members: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [members, setMembers] = useState<MembersMembership[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<MembersMembership[]>([]);
  const [paginatedMembers, setPaginatedMembers] = useState<MembersMembership[]>([]);
  const [membershipPlans, setMembershipPlans] = useState<MembershipPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showReceiptsModal, setShowReceiptsModal] = useState(false);
  const [showEditStartDateModal, setShowEditStartDateModal] = useState(false);
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<MembersMembership | null>(null);
  const [freezeHistory, setFreezeHistory] = useState<any[]>([]);
  const [activeFreeze, setActiveFreeze] = useState<any | null>(null);
  const [memberReceipts, setMemberReceipts] = useState<any[]>([]);
  const [pastMemberships, setPastMemberships] = useState<any[]>([]);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [activeModalTab, setActiveModalTab] = useState<'receipts' | 'memberships' | 'editDetails'>('receipts');
  
  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogMessage, setConfirmDialogMessage] = useState('');
  const [confirmDialogAction, setConfirmDialogAction] = useState<(() => void) | null>(null);
  
  // Toast notifications
  const { toasts, removeToast, success, error, warning, info } = useToast();
  
  // Confirmation dialog helper
  const showConfirmation = (message: string, onConfirm: () => void) => {
    setConfirmDialogMessage(message);
    setConfirmDialogAction(() => onConfirm);
    setShowConfirmDialog(true);
  };
  
  const handleConfirmAction = () => {
    if (confirmDialogAction) {
      confirmDialogAction();
    }
    setShowConfirmDialog(false);
    setConfirmDialogAction(null);
  };
  
  // Search and Filter state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('all'); // all, active, expiringSoon, expired, pendingPayments, pastMembers
  const [statusFilter, setStatusFilter] = useState<string>('all'); // all, active, inactive, expired
  const [paymentFilter, setPaymentFilter] = useState<string>('all'); // all, paid, pending
  const [planFilter, setPlanFilter] = useState<string>('all'); // all, or plan ID
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(25);

  useEffect(() => {
    fetchMembers();
    fetchMembershipPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    if (openDropdown !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  // Apply filters and search
  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, searchQuery, activeTab, statusFilter, paymentFilter, planFilter]);

  // Apply pagination
  useEffect(() => {
    applyPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredMembers, currentPage, itemsPerPage]);

  // Handle opening member details from dashboard notification
  useEffect(() => {
    const state = location.state as { openMemberId?: number };
    if (state?.openMemberId && members.length > 0) {
      const member = members.find(m => m.membersMembershipId === state.openMemberId);
      if (member) {
        handleViewReceipts(member);
        // Clear the state to prevent reopening on refresh
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [members, location.state]);

  const fetchMembers = async () => {
    try {
      let data;
      // Fetch based on active tab
      switch (activeTab) {
        case 'active':
          data = await membersMembershipAPI.getActive();
          break;
        case 'expiringSoon':
          data = await membersMembershipAPI.getExpiringSoon();
          break;
        case 'expired':
          data = await membersMembershipAPI.getExpired();
          break;
        case 'pendingPayments':
          data = await membersMembershipAPI.getPendingPayments();
          break;
        case 'pastMembers':
          data = await membersMembershipAPI.getPastMembers();
          break;
        default:
          data = await membersMembershipAPI.getAll();
      }
      setMembers(data);
    } catch (err) {
      error('Failed to fetch members');
      console.error('Error fetching members:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...members];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(m =>
        `${m.enquiry?.firstName} ${m.enquiry?.lastName}`.toLowerCase().includes(query) ||
        m.enquiry?.email?.toLowerCase().includes(query) ||
        m.enquiry?.phone?.toLowerCase().includes(query) ||
        m.membershipPlan?.planName?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'active') {
        filtered = filtered.filter(m => m.isActive);
      } else if (statusFilter === 'inactive') {
        filtered = filtered.filter(m => !m.isActive);
      } else if (statusFilter === 'expired') {
        const now = new Date();
        filtered = filtered.filter(m => {
          const endDate = new Date(m.startDate);
          endDate.setMonth(endDate.getMonth() + (m.membershipPlan?.durationInMonths || 0));
          return endDate < now && !m.isInactive;
        });
      }
    }

    // Payment filter
    if (paymentFilter !== 'all') {
      if (paymentFilter === 'paid') {
        filtered = filtered.filter(m => m.remainingAmount <= 0);
      } else if (paymentFilter === 'pending') {
        filtered = filtered.filter(m => m.remainingAmount > 0);
      }
    }

    // Plan filter
    if (planFilter !== 'all') {
      filtered = filtered.filter(m => m.membershipPlanId.toString() === planFilter);
    }

    setFilteredMembers(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const applyPagination = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedMembers(filteredMembers.slice(startIndex, endIndex));
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPaymentFilter('all');
    setPlanFilter('all');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);

  const fetchMembershipPlans = async () => {
    try {
      const data = await membershipPlanAPI.getActive();
      setMembershipPlans(data);
    } catch (error) {
      console.error('Error fetching membership plans:', error);
    }
  };

  const handlePayment = (member: MembersMembership) => {
    setSelectedMember(member);
    setShowPaymentModal(true);
  };

  const handleUpgrade = (member: MembersMembership) => {
    setSelectedMember(member);
    setShowUpgradeModal(true);
  };

  const handleFreezeMembership = async (member: MembersMembership) => {
    setSelectedMember(member);
    
    // Check if already frozen
    try {
      const freeze = await membershipFreezeAPI.getActiveFreeze(member.membersMembershipId);
      if (freeze) {
        setActiveFreeze(freeze);
        warning('⚠️ This membership is already frozen!');
        return;
      }
    } catch (err: any) {
      // No active freeze found, which is good
    }
    
    setShowFreezeModal(true);
  };

  const freezeMembership = async (
    freezeReason: string,
    freezeNotes: string,
    freezeStartDate: string,
    freezeEndDate: string
  ) => {
    if (!selectedMember) return;
    
    try {
      await membershipFreezeAPI.create({
        membersMembershipId: selectedMember.membersMembershipId,
        freezeReason,
        freezeNotes,
        freezeStartDate,
        freezeEndDate,
        requestedBy: 'admin@gym.com'
      });
      
      setShowFreezeModal(false);
      fetchMembers();
      success('❄️ Membership frozen successfully! End date has been extended.');
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to freeze membership');
      console.error('Error freezing membership:', err);
    }
  };

  const handleUnfreeze = async (member: MembersMembership) => {
    try {
      const freeze = await membershipFreezeAPI.getActiveFreeze(member.membersMembershipId);
      if (!freeze) {
        error('No active freeze found for this membership');
        return;
      }
      
      showConfirmation(
        'Are you sure you want to unfreeze this membership?',
        async () => {
          try {
            await membershipFreezeAPI.unfreeze(freeze.membershipFreezeId);
            fetchMembers();
            success('✅ Membership unfrozen successfully!');
          } catch (err: any) {
            error('Failed to unfreeze membership');
            console.error('Error unfreezing:', err);
          }
        }
      );
    } catch (err: any) {
      error('Failed to check freeze status');
      console.error('Error checking freeze:', err);
    }
  };

  const handleEditStartDate = (member: MembersMembership) => {
    setSelectedMember(member);
    setShowEditStartDateModal(true);
  };

  const handleStartDateUpdate = async (memberId: number, newDate: string) => {
    try {
      await membersMembershipAPI.updateStartDate(memberId, newDate);
      success('✅ Start date updated successfully!');
      setShowEditStartDateModal(false);
      fetchMembers();
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to update start date');
    }
  };

  const handleToggleStatus = async (member: MembersMembership) => {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Admin';
    const newStatus = !member.isActive;
    
    showConfirmation(
      `Are you sure you want to ${newStatus ? 'activate' : 'deactivate'} this membership?`,
      async () => {
        try {
          await membersMembershipAPI.toggleStatus(member.membersMembershipId, !newStatus, userName);
          success(`Membership ${newStatus ? 'activated' : 'deactivated'} successfully`);
          fetchMembers();
        } catch (err: any) {
          error(err.response?.data?.message || 'Failed to toggle membership status');
          console.error('Error toggling status:', err);
        }
      }
    );
  };

  const handleDelete = async (member: MembersMembership) => {
    const memberName = `${member.enquiry?.firstName} ${member.enquiry?.lastName}`;
    
    showConfirmation(
      `Are you sure you want to DELETE the membership for ${memberName}? This action cannot be undone!`,
      async () => {
        try {
          await membersMembershipAPI.delete(member.membersMembershipId);
          success('Membership deleted successfully');
          fetchMembers();
        } catch (err: any) {
          error(err.response?.data?.message || 'Failed to delete membership');
          console.error('Error deleting membership:', err);
        }
      }
    );
  };

  const processPayment = async (
    amount: number,
    discount: number,
    paymentMode: string, 
    transactionId: string, 
    expectedDate: string,
    notes: string
  ) => {
    if (!selectedMember) return;
    
    try {
      await membersMembershipAPI.processPayment(
        selectedMember.membersMembershipId, 
        amount,
        discount,
        paymentMode,
        transactionId,
        expectedDate,
        notes
      );
      setShowPaymentModal(false);
      fetchMembers();
      success('✅ Payment processed successfully! Receipt sent via email.');
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to process payment');
      console.error('Error processing payment:', err);
    }
  };

  const handleViewReceipts = async (member: MembersMembership) => {
    setSelectedMember(member);
    setActiveModalTab('receipts');
    try {
      // Fetch both receipts and past memberships
      console.log('Fetching data for member:', member.membersMembershipId, 'enquiryId:', member.enquiryId);
      
      const [receipts, memberships] = await Promise.all([
        paymentReceiptAPI.getByMember(member.membersMembershipId),
        membersMembershipAPI.getByEnquiry(member.enquiryId)
      ]);
      
      console.log('Receipts:', receipts);
      console.log('Memberships:', memberships);
      
      setMemberReceipts(receipts);
      setPastMemberships(memberships);
      setShowReceiptsModal(true);
    } catch (err: any) {
      console.error('Error fetching member data:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      error(`Failed to load member data: ${err.response?.data?.message || err.message || 'Unknown error'}`);
    }
  };

  const handleDownloadReceipt = async (receiptId: number, receiptNumber: string) => {
    try {
      const { blob } = await paymentReceiptAPI.downloadReceipt(receiptId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Receipt_${receiptNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      success('Receipt downloaded successfully!');
    } catch (err: any) {
      const errorMessage = err?.message || err?.response?.data?.message || 'Failed to download receipt. Please try again or contact support.';
      error(errorMessage);
      console.error('Error downloading receipt:', err);
    }
  };

  const handleResendReceiptEmail = async (receiptId: number, receiptNumber: string) => {
    if (!selectedMember?.enquiry?.email) {
      error('Member email address is not available. Please update member details first.');
      return;
    }

    showConfirmation(
      `Resend receipt ${receiptNumber} to ${selectedMember.enquiry.email}?`,
      async () => {
        try {
          const result = await paymentReceiptAPI.resendEmail(receiptId);
          success(`✅ ${result.message}`);
          // Refresh receipts to show updated email sent status
          const receipts = await paymentReceiptAPI.getByMember(selectedMember.membersMembershipId);
          setMemberReceipts(receipts);
        } catch (err: any) {
          error(err.response?.data?.message || 'Failed to resend email');
          console.error('Error resending email:', err);
        }
      }
    );
  };

  const handleViewReceiptInBrowser = async (receiptId: number) => {
    try {
      // Download receipt (PDF or HTML fallback)
      const { blob } = await paymentReceiptAPI.downloadReceipt(receiptId);
      
      // Create object URL from blob
      const url = window.URL.createObjectURL(blob);
      
      // Open in new tab
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        warning('Please allow pop-ups to view the receipt');
        window.URL.revokeObjectURL(url);
        return;
      }
      
      // PDF is always returned (HTML is converted automatically)
      
      // Clean up the object URL after a delay (when the window is closed)
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (err: any) {
      const errorMessage = err?.message || err?.response?.data?.message || 'Failed to load receipt. Please try again or contact support.';
      error(errorMessage);
      console.error('Error viewing receipt:', err);
    }
  };

  const processUpgrade = async (newPlanId: number, paidAmount: number) => {
    if (!selectedMember) return;
    
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Admin';
    
    try {
      await membersMembershipAPI.upgrade(selectedMember.membersMembershipId, newPlanId, paidAmount, userName);
      success('✅ Membership upgraded successfully!');
      setShowUpgradeModal(false);
      fetchMembers();
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to upgrade membership');
      console.error('Error upgrading membership:', err);
    }
  };

  const handleExportToExcel = async () => {
    try {
      const response = await membersMembershipAPI.exportToExcel();
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Members_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      success('✅ Members exported to Excel successfully!');
    } catch (err: any) {
      console.error('Error exporting to Excel:', err);
      error('Failed to export members to Excel');
    }
  };

  const getStatusBadge = (member: MembersMembership) => {
    // Only show membership status (Active/Inactive)
    const membershipStatus = member.isActive ? 'Active' : 'Inactive';
    const statusColor = member.isActive ? '#10B981' : '#EF4444';
    const statusBgColor = member.isActive ? '#D1FAE5' : '#FEE2E2';
    
    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        backgroundColor: statusBgColor,
        color: statusColor,
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-block',
        textAlign: 'center'
      }}>
        {membershipStatus}
      </span>
    );
  };

  if (loading) {
    return <div className="loading">Loading members...</div>;
  }

  return (
    <div>
      <div className="header">
        <h1 className="header-title">Members</h1>
        <button className="btn btn-success" onClick={handleExportToExcel}>
          <Download size={20} style={{ marginRight: '0.5rem' }} />
          Export to Excel
        </button>
      </div>

      {/* Tabs */}
      <div style={{ 
        borderBottom: '2px solid #E5E7EB', 
        marginBottom: '1.5rem',
        display: 'flex',
        gap: '2rem',
        backgroundColor: 'white',
        padding: '0 1rem'
      }}>
        {[
          { id: 'all', label: 'All Members' },
          { id: 'active', label: 'Active' },
          { id: 'expiringSoon', label: 'Expiring Soon' },
          { id: 'expired', label: 'Expired' },
          { id: 'pendingPayments', label: 'Pending Payments' },
          { id: 'pastMembers', label: 'Past Members' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              setCurrentPage(1);
            }}
            style={{
              padding: '0.75rem 1rem',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid #4F46E5' : '2px solid transparent',
              color: activeTab === tab.id ? '#4F46E5' : '#6B7280',
              fontWeight: activeTab === tab.id ? '600' : '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '0.875rem',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-body">
          <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Filter size={20} />
            Search & Filters
          </h3>

          {/* Search Bar */}
          <div style={{ marginBottom: '1rem' }}>
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search by name, email, phone, or plan..."
              onClear={() => setSearchQuery('')}
            />
          </div>

          {/* Filter Options */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            {/* Status Filter */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem', color: '#374151' }}>
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="expired">Expired</option>
              </select>
            </div>

            {/* Payment Filter */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem', color: '#374151' }}>
                Payment Status
              </label>
              <select
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Payments</option>
                <option value="paid">Fully Paid</option>
                <option value="pending">Payment Pending</option>
              </select>
            </div>

            {/* Plan Filter */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem', color: '#374151' }}>
                Membership Plan
              </label>
              <select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Plans</option>
                {membershipPlans.map((plan) => (
                  <option key={plan.membershipPlanId} value={plan.membershipPlanId}>
                    {plan.planName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Summary and Clear Button */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            paddingTop: '1rem',
            borderTop: '1px solid #E5E7EB'
          }}>
            <span style={{ color: '#6B7280', fontSize: '0.875rem' }}>
              Showing <strong>{filteredMembers.length}</strong> of <strong>{members.length}</strong> members
            </span>
            {(searchQuery || statusFilter !== 'all' || paymentFilter !== 'all' || planFilter !== 'all') && (
              <button
                onClick={clearFilters}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#EF4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                Clear All Filters
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {paginatedMembers.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '3rem 2rem',
              color: '#6B7280'
            }}>
              <Users size={64} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>No Members Found</h3>
              <p style={{ margin: 0, fontSize: '0.875rem' }}>
                {activeTab === 'active' 
                  ? 'No active members found. Try adjusting your filters or check other tabs.'
                  : activeTab === 'expiringSoon'
                  ? 'No members expiring soon.'
                  : activeTab === 'expired'
                  ? 'No expired members found.'
                  : activeTab === 'pendingPayments'
                  ? 'No members with pending payments.'
                  : activeTab === 'pastMembers'
                  ? 'No past members found.'
                  : 'No members match your current filters. Try adjusting your search or filters.'}
              </p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Member</th>
                    <th>Plan</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Total Amount</th>
                    <th>Paid</th>
                    <th>Remaining</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedMembers.map((member) => {
                    const endDate = new Date(member.startDate);
                    endDate.setMonth(endDate.getMonth() + (member.membershipPlan?.durationInMonths || 0));
                    
                    const memberName = member.enquiry 
                      ? `${member.enquiry.firstName || ''} ${member.enquiry.lastName || ''}`.trim()
                      : `Member #${member.membersMembershipId}`;
                    
                    return (
                      <tr key={member.membersMembershipId}>
                        <td>
                          <span
                            onClick={() => handleViewReceipts(member)}
                            style={{
                              cursor: 'pointer',
                              color: '#4F46E5',
                              fontWeight: '500',
                              textDecoration: 'none',
                              transition: 'color 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#6366F1';
                              e.currentTarget.style.textDecoration = 'underline';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = '#4F46E5';
                              e.currentTarget.style.textDecoration = 'none';
                            }}
                            title="Click to view payment receipts"
                          >
                            {memberName || 'N/A'}
                          </span>
                        </td>
                        <td>{member.membershipPlan?.planName || 'N/A'}</td>
                        <td>{new Date(member.startDate).toLocaleDateString()}</td>
                        <td>{endDate.toLocaleDateString()}</td>
                        <td>₹{member.totalAmount?.toFixed(2) || '0.00'}</td>
                        <td>₹{member.paidAmount?.toFixed(2) || '0.00'}</td>
                        <td>₹{member.remainingAmount?.toFixed(2) || '0.00'}</td>
                        <td>{getStatusBadge(member)}</td>
                      <td>
                        <div style={{ position: 'relative' }}>
                          <button 
                            className="btn btn-secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdown(openDropdown === member.membersMembershipId ? null : member.membersMembershipId);
                            }}
                            style={{ padding: '0.25rem 0.5rem' }}
                            title="Actions"
                          >
                            <MoreVertical size={16} />
                          </button>
                          
                          {openDropdown === member.membersMembershipId && (
                            <div 
                              style={{
                                position: 'absolute',
                                right: 0,
                                top: '100%',
                                marginTop: '0.25rem',
                                backgroundColor: 'white',
                                border: '1px solid #E5E7EB',
                                borderRadius: '6px',
                                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                zIndex: 1000,
                                minWidth: '200px',
                                overflow: 'hidden'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {member.remainingAmount > 0 && (
                                <button
                                  onClick={() => {
                                    handlePayment(member);
                                    setOpenDropdown(null);
                                  }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    width: '100%',
                                    padding: '0.75rem 1rem',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    color: '#374151',
                                    transition: 'background-color 0.15s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                  <DollarSign size={16} style={{ color: '#10B981' }} />
                                  Process Payment
                                </button>
                              )}
                              
                              <button
                                onClick={() => {
                                  handleEditStartDate(member);
                                  setOpenDropdown(null);
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.75rem',
                                  width: '100%',
                                  padding: '0.75rem 1rem',
                                  backgroundColor: 'transparent',
                                  border: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  fontSize: '0.875rem',
                                  color: '#374151',
                                  transition: 'background-color 0.15s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <Calendar size={16} style={{ color: '#6B7280' }} />
                                Edit Start Date
                              </button>
                              
                              <button
                                onClick={() => {
                                  handleUpgrade(member);
                                  setOpenDropdown(null);
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.75rem',
                                  width: '100%',
                                  padding: '0.75rem 1rem',
                                  backgroundColor: 'transparent',
                                  border: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  fontSize: '0.875rem',
                                  color: '#374151',
                                  transition: 'background-color 0.15s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <ArrowUpCircle size={16} style={{ color: '#4F46E5' }} />
                                Upgrade Membership
                              </button>
                              
                              <button
                                onClick={() => {
                                  handleFreezeMembership(member);
                                  setOpenDropdown(null);
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  width: '100%',
                                  padding: '0.5rem 1rem',
                                  border: 'none',
                                  backgroundColor: 'transparent',
                                  cursor: 'pointer',
                                  fontSize: '0.875rem',
                                  textAlign: 'left',
                                  borderRadius: '4px',
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <Snowflake size={16} style={{ color: '#3B82F6' }} />
                                Freeze Membership
                              </button>
                              
                              <button
                                onClick={() => {
                                  handleToggleStatus(member);
                                  setOpenDropdown(null);
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.75rem',
                                  width: '100%',
                                  padding: '0.75rem 1rem',
                                  backgroundColor: 'transparent',
                                  border: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  fontSize: '0.875rem',
                                  color: '#374151',
                                  transition: 'background-color 0.15s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                {member.isActive ? (
                                  <>
                                    <ToggleRight size={16} style={{ color: '#F59E0B' }} />
                                    Deactivate
                                  </>
                                ) : (
                                  <>
                                    <ToggleLeft size={16} style={{ color: '#3B82F6' }} />
                                    Activate
                                  </>
                                )}
                              </button>
                              
                              <button
                                onClick={() => {
                                  handleDelete(member);
                                  setOpenDropdown(null);
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.75rem',
                                  width: '100%',
                                  padding: '0.75rem 1rem',
                                  backgroundColor: 'transparent',
                                  border: 'none',
                                  borderTop: '1px solid #E5E7EB',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  fontSize: '0.875rem',
                                  color: '#DC2626',
                                  transition: 'background-color 0.15s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FEE2E2'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <Trash2 size={16} />
                                Delete Membership
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination */}
          {filteredMembers.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredMembers.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedMember && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Process Payment</h2>
              <button className="modal-close" onClick={() => setShowPaymentModal(false)}>×</button>
            </div>
            
            {/* Member Info */}
            <div style={{ 
              marginBottom: '1.5rem', 
              padding: '1rem', 
              backgroundColor: '#F3F4F6',
              borderRadius: '8px'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <strong style={{ color: '#374151' }}>Member:</strong>
                  <div>{selectedMember.enquiry?.firstName} {selectedMember.enquiry?.lastName}</div>
                </div>
                <div>
                  <strong style={{ color: '#374151' }}>Plan:</strong>
                  <div>{selectedMember.membershipPlan?.planName}</div>
                </div>
                <div>
                  <strong style={{ color: '#374151' }}>Total Amount:</strong>
                  <div>₹{selectedMember.totalAmount.toFixed(2)}</div>
                </div>
                <div>
                  <strong style={{ color: '#374151' }}>Paid:</strong>
                  <div style={{ color: '#059669' }}>₹{selectedMember.paidAmount.toFixed(2)}</div>
                </div>
                <div>
                  <strong style={{ color: '#DC2626' }}>Due Amount:</strong>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#DC2626' }}>
                    ₹{selectedMember.remainingAmount.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Payment Amount */}
            <div className="form-group">
              <label className="form-label" htmlFor="paymentAmount">Payment Amount *</label>
              <input
                type="number"
                className="form-input"
                id="paymentAmount"
                placeholder="0.00"
                step="0.01"
                max={selectedMember.remainingAmount}
                style={{ fontSize: '1rem' }}
              />
            </div>

            {/* Discount */}
            <div className="form-group">
              <label className="form-label" htmlFor="discount">Discount Amount</label>
              <input
                type="number"
                className="form-input"
                id="discount"
                placeholder="0.00"
                step="0.01"
                min="0"
                defaultValue="0"
                style={{ fontSize: '1rem' }}
              />
              <small style={{ color: '#6B7280', fontSize: '0.75rem' }}>
                💰 Enter discount amount if applicable (e.g., promotional discount, referral bonus, etc.)
              </small>
            </div>

            {/* Payment Mode Dropdown */}
            <div className="form-group">
              <label className="form-label" htmlFor="paymentMode">Payment Mode *</label>
              <select
                id="paymentMode"
                className="form-input"
                style={{ fontSize: '0.875rem' }}
              >
                <option value="">-- Select Payment Mode --</option>
                <option value="Cash">Cash</option>
                <option value="UPI">UPI</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Debit Card">Debit Card</option>
                <option value="Net Banking">Net Banking</option>
                <option value="Cheque">Cheque</option>
                <option value="Bank Transfer">Bank Transfer</option>
              </select>
            </div>

            {/* Transaction ID */}
            <div className="form-group">
              <label className="form-label" htmlFor="transactionId">Transaction ID / Reference Number</label>
              <input
                type="text"
                className="form-input"
                id="transactionId"
                placeholder="Enter transaction ID or reference number"
              />
            </div>

            {/* Expected Date */}
            <div className="form-group">
              <label className="form-label" htmlFor="expectedDate">Next Due Date (Expected Date for Remaining Payment)</label>
              <input
                type="date"
                className="form-input"
                id="expectedDate"
                min={new Date().toISOString().split('T')[0]}
              />
              <small style={{ color: '#6B7280', fontSize: '0.75rem' }}>
                When do you expect the next payment? (Leave empty to default to 1 month from today)
              </small>
            </div>

            {/* Notes */}
            <div className="form-group">
              <label className="form-label" htmlFor="paymentNotes">Notes (Optional)</label>
              <textarea
                id="paymentNotes"
                className="form-input"
                rows={3}
                placeholder="Add any additional notes..."
                style={{ resize: 'vertical' }}
              />
            </div>
            
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowPaymentModal(false)}>
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-success"
                onClick={() => {
                  const amount = parseFloat((document.getElementById('paymentAmount') as HTMLInputElement).value) || 0;
                  const discount = parseFloat((document.getElementById('discount') as HTMLInputElement).value) || 0;
                  const paymentMode = (document.getElementById('paymentMode') as HTMLSelectElement).value;
                  const transactionId = (document.getElementById('transactionId') as HTMLInputElement).value;
                  const expectedDate = (document.getElementById('expectedDate') as HTMLInputElement).value;
                  const notes = (document.getElementById('paymentNotes') as HTMLTextAreaElement).value;
                  
                  if (amount <= 0 || amount > selectedMember.remainingAmount) {
                    error('Please enter a valid payment amount');
                    return;
                  }

                  if (discount < 0) {
                    error('Discount cannot be negative');
                    return;
                  }
                  
                  if (!paymentMode) {
                    error('Please select a payment mode');
                    return;
                  }
                  
                  processPayment(amount, discount, paymentMode, transactionId, expectedDate, notes);
                }}
              >
                Process Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Freeze Membership Modal */}
      {showFreezeModal && selectedMember && (
        <div className="modal-overlay" onClick={() => setShowFreezeModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2 className="modal-title">
                <Snowflake size={24} style={{ marginRight: '0.5rem', color: '#3B82F6' }} />
                Freeze Membership
              </h2>
              <button className="modal-close" onClick={() => setShowFreezeModal(false)}>×</button>
            </div>
            
            <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#EFF6FF', borderRadius: '8px', border: '1px solid #DBEAFE' }}>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>Member:</strong> {selectedMember.enquiry?.firstName} {selectedMember.enquiry?.lastName}
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>Current Plan:</strong> {selectedMember.membershipPlan?.planName}
              </div>
              <div>
                <strong>Current End Date:</strong> {new Date(selectedMember.endDate).toLocaleDateString()}
              </div>
            </div>

            <div style={{ backgroundColor: '#FEF3C7', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid #FCD34D' }}>
              <strong style={{ color: '#92400E' }}>ℹ️ Note:</strong>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#78350F' }}>
                When you freeze a membership, the end date will automatically be extended by the number of freeze days.
                The member won't be charged or expected to attend during the freeze period.
              </p>
            </div>
            
            <div className="form-group">
              <label className="form-label" htmlFor="freezeReason">Freeze Reason *</label>
              <select
                id="freezeReason"
                className="form-input"
                defaultValue=""
                required
              >
                <option value="" disabled>-- Select Reason --</option>
                <option value="Medical">Medical</option>
                <option value="Vacation">Vacation/Travel</option>
                <option value="Personal">Personal</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="freezeStartDate">Freeze Start Date *</label>
              <input
                type="date"
                id="freezeStartDate"
                className="form-input"
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="freezeEndDate">Freeze End Date *</label>
              <input
                type="date"
                id="freezeEndDate"
                className="form-input"
                min={new Date().toISOString().split('T')[0]}
                required
              />
              <small style={{ color: '#6B7280', fontSize: '0.75rem' }}>
                📅 The membership end date will be extended by the number of days between start and end dates
              </small>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="freezeNotes">Additional Notes</label>
              <textarea
                id="freezeNotes"
                className="form-input"
                rows={3}
                placeholder="Add any additional information about the freeze reason..."
                style={{ resize: 'vertical' }}
              />
            </div>
            
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowFreezeModal(false)}>
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={() => {
                  const freezeReason = (document.getElementById('freezeReason') as HTMLSelectElement).value;
                  const freezeStartDate = (document.getElementById('freezeStartDate') as HTMLInputElement).value;
                  const freezeEndDate = (document.getElementById('freezeEndDate') as HTMLInputElement).value;
                  const freezeNotes = (document.getElementById('freezeNotes') as HTMLTextAreaElement).value;
                  
                  if (!freezeReason || !freezeStartDate || !freezeEndDate) {
                    error('Please fill in all required fields');
                    return;
                  }

                  if (new Date(freezeEndDate) <= new Date(freezeStartDate)) {
                    error('End date must be after start date');
                    return;
                  }
                  
                  freezeMembership(freezeReason, freezeNotes, freezeStartDate, freezeEndDate);
                }}
                style={{ backgroundColor: '#3B82F6' }}
              >
                <Snowflake size={16} style={{ marginRight: '0.5rem' }} />
                Freeze Membership
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && selectedMember && (
        <div className="modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Upgrade Membership</h2>
              <button className="modal-close" onClick={() => setShowUpgradeModal(false)}>×</button>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <strong>Member:</strong> {selectedMember.enquiry?.firstName} {selectedMember.enquiry?.lastName}<br />
              <strong>Current Plan:</strong> {selectedMember.membershipPlan?.planName}<br />
              <strong>Current Amount:</strong> ₹{selectedMember.totalAmount}
            </div>
            
            <div className="form-group">
              <label className="form-label">New Membership Plan</label>
              <select
                className="form-input"
                id="newPlanId"
              >
                <option value="">Select Plan</option>
                {membershipPlans.map((plan) => (
                  <option key={plan.membershipPlanId} value={plan.membershipPlanId}>
                    {plan.planName} - ₹{plan.price} ({plan.durationInMonths} months)
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label className="form-label">Initial Payment Amount</label>
              <input
                type="number"
                className="form-input"
                id="upgradePaidAmount"
                placeholder="0.00"
                step="0.01"
              />
            </div>
            
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowUpgradeModal(false)}>
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={() => {
                  const planId = parseInt((document.getElementById('newPlanId') as HTMLSelectElement).value);
                  const paidAmount = parseFloat((document.getElementById('upgradePaidAmount') as HTMLInputElement).value) || 0;
                  
                  if (!planId) {
                    error('Please select a membership plan');
                    return;
                  }
                  
                  if (paidAmount < 0) {
                    error('Payment amount cannot be negative');
                    return;
                  }
                  
                  processUpgrade(planId, paidAmount);
                }}
              >
                Upgrade Membership
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Member Details Modal (Receipts & Past Memberships) */}
      {showReceiptsModal && selectedMember && (
        <div className="modal-overlay" onClick={() => setShowReceiptsModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <div className="modal-header">
              <h2 className="modal-title">
                Member Details - {selectedMember.enquiry?.firstName} {selectedMember.enquiry?.lastName}
              </h2>
              <button className="modal-close" onClick={() => setShowReceiptsModal(false)}>×</button>
            </div>
            
            {/* Tabs */}
            <div style={{ 
              borderBottom: '2px solid #E5E7EB', 
              display: 'flex',
              gap: '2rem',
              padding: '0 1.5rem',
              backgroundColor: 'white'
            }}>
              <button
                onClick={() => setActiveModalTab('receipts')}
                style={{
                  padding: '0.75rem 1rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: activeModalTab === 'receipts' ? '2px solid #4F46E5' : '2px solid transparent',
                  color: activeModalTab === 'receipts' ? '#4F46E5' : '#6B7280',
                  fontWeight: activeModalTab === 'receipts' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontSize: '0.875rem',
                }}
              >
                Active Receipts ({memberReceipts.length})
              </button>
              <button
                onClick={() => setActiveModalTab('memberships')}
                style={{
                  padding: '0.75rem 1rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: activeModalTab === 'memberships' ? '2px solid #4F46E5' : '2px solid transparent',
                  color: activeModalTab === 'memberships' ? '#4F46E5' : '#6B7280',
                  fontWeight: activeModalTab === 'memberships' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontSize: '0.875rem',
                }}
              >
                Membership History ({pastMemberships.length})
              </button>
              <button
                onClick={() => setActiveModalTab('editDetails')}
                style={{
                  padding: '0.75rem 1rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: activeModalTab === 'editDetails' ? '2px solid #4F46E5' : '2px solid transparent',
                  color: activeModalTab === 'editDetails' ? '#4F46E5' : '#6B7280',
                  fontWeight: activeModalTab === 'editDetails' ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontSize: '0.875rem',
                }}
              >
                Edit Member Details
              </button>
            </div>
            
            <div style={{ padding: '1.5rem' }}>
              {/* Payment Receipts Tab */}
              {activeModalTab === 'receipts' && (
                <>
                  {/* Active Membership Info */}
                  <div style={{ 
                    marginBottom: '1rem', 
                    padding: '1rem', 
                    backgroundColor: '#EEF2FF',
                    borderRadius: '6px',
                    border: '1px solid #4F46E5'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.75rem',
                        backgroundColor: '#4F46E5',
                        color: 'white',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        ACTIVE MEMBERSHIP
                      </span>
                      <span style={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: '500' }}>
                        {selectedMember.membershipPlan?.planName}
                      </span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.875rem', color: '#374151' }}>
                      <div>
                        <strong>Start Date:</strong> {new Date(selectedMember.startDate).toLocaleDateString()}
                      </div>
                      <div>
                        <strong>End Date:</strong> {(() => {
                          const startDate = new Date(selectedMember.startDate);
                          const endDate = new Date(startDate);
                          endDate.setMonth(endDate.getMonth() + (selectedMember.membershipPlan?.durationInMonths || 0));
                          return endDate.toLocaleDateString();
                        })()}
                      </div>
                    </div>
                  </div>

                  {memberReceipts.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                      <Receipt size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                      <p>No payment receipts found for this active membership.</p>
                    </div>
                  ) : (
                    <div>
                      <p style={{ marginBottom: '1rem', color: '#666' }}>
                        Active Membership Receipts: <strong>{memberReceipts.length}</strong>
                      </p>
                      
                      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <table className="table" style={{ marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th>Receipt #</th>
                              <th>Date</th>
                              <th>Amount Paid</th>
                              <th>Method</th>
                              <th>Email Sent</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {memberReceipts.map((receipt) => (
                              <tr key={receipt.paymentReceiptId}>
                                <td><strong>{receipt.receiptNumber}</strong></td>
                                <td>{new Date(receipt.paymentDate + (receipt.paymentDate.includes('Z') ? '' : 'Z')).toLocaleString()}</td>
                                <td><strong style={{ color: '#28a745' }}>₹{receipt.amountPaid.toFixed(2)}</strong></td>
                                <td>{receipt.paymentMethod || 'Cash'}</td>
                                <td>
                                  {receipt.emailSent ? (
                                    <span style={{ color: '#28a745' }}>✓ Sent</span>
                                  ) : (
                                    <span style={{ color: '#dc3545' }}>✗ Not Sent</span>
                                  )}
                                </td>
                                <td>
                                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <button
                                      className="btn btn-primary"
                                      onClick={() => handleViewReceiptInBrowser(receipt.paymentReceiptId)}
                                      style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
                                      title="View Receipt"
                                    >
                                      <Eye size={14} />
                                    </button>
                                    <button
                                      className="btn btn-success"
                                      onClick={() => handleDownloadReceipt(receipt.paymentReceiptId, receipt.receiptNumber)}
                                      style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
                                      title="Download Receipt"
                                    >
                                      <Download size={14} />
                                    </button>
                                    <button
                                      className="btn btn-info"
                                      onClick={() => handleResendReceiptEmail(receipt.paymentReceiptId, receipt.receiptNumber)}
                                      style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
                                      title="Resend Email"
                                    >
                                      <Mail size={14} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      <div style={{ 
                        marginTop: '1.5rem', 
                        padding: '1rem', 
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px'
                      }}>
                        <h4 style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '1rem', color: '#374151' }}>
                          Payment Summary - {selectedMember.membershipPlan?.planName}
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                          <div>
                            <small style={{ color: '#666', fontSize: '0.75rem' }}>Total Paid</small>
                            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#28a745' }}>
                              ₹{memberReceipts.reduce((sum, r) => sum + r.amountPaid, 0).toFixed(2)}
                            </div>
                          </div>
                          <div>
                            <small style={{ color: '#666', fontSize: '0.75rem' }}>Total Amount</small>
                            <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                              ₹{selectedMember.totalAmount.toFixed(2)}
                            </div>
                          </div>
                          <div>
                            <small style={{ color: '#666', fontSize: '0.75rem' }}>Remaining</small>
                            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#dc3545' }}>
                              ₹{selectedMember.remainingAmount.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Membership History Tab */}
              {activeModalTab === 'memberships' && (
                <>
                  {pastMemberships.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                      <Calendar size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                      <p>No membership history found.</p>
                    </div>
                  ) : (
                    <div>
                      <div style={{ 
                        marginBottom: '1rem', 
                        padding: '0.75rem 1rem', 
                        backgroundColor: '#F3F4F6',
                        borderRadius: '6px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span style={{ color: '#374151', fontWeight: '500' }}>
                          Past Memberships: <strong>{(() => {
                            const uniqueMemberships = pastMemberships.filter((membership, index, self) => 
                              index === self.findIndex(m => m.membersMembershipId === membership.membersMembershipId)
                            );
                            // Exclude active membership from count
                            const pastOnly = uniqueMemberships.filter(m => m.membersMembershipId !== selectedMember.membersMembershipId);
                            return pastOnly.length;
                          })()}</strong>
                        </span>
                        <span style={{ 
                          fontSize: '0.875rem', 
                          color: '#6B7280',
                          padding: '0.25rem 0.75rem',
                          backgroundColor: '#EEF2FF',
                          borderRadius: '9999px'
                        }}>
                          Past Memberships Only
                        </span>
                      </div>
                      
                      {/* Filter out duplicates and active membership */}
                      {(() => {
                        // First filter duplicates
                        const uniqueMemberships = pastMemberships.filter((membership, index, self) => 
                          index === self.findIndex(m => m.membersMembershipId === membership.membersMembershipId)
                        );
                        
                        // Then filter out the active/current membership
                        const pastMembershipsOnly = uniqueMemberships.filter(
                          membership => membership.membersMembershipId !== selectedMember.membersMembershipId
                        );
                        
                        return (
                          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <table className="table" style={{ marginBottom: 0 }}>
                              <thead>
                                <tr>
                                  <th>Plan</th>
                                  <th>Start Date</th>
                                  <th>End Date</th>
                                  <th>Duration</th>
                                  <th>Total Amount</th>
                                  <th>Paid</th>
                                  <th>Remaining</th>
                                  <th>Status</th>
                                  <th>Created By</th>
                                </tr>
                              </thead>
                              <tbody>
                                {pastMembershipsOnly.length === 0 ? (
                                  <tr>
                                    <td colSpan={9} style={{ padding: '2rem', textAlign: 'center', color: '#6B7280' }}>
                                      No past memberships found. This is the first membership.
                                    </td>
                                  </tr>
                                ) : (
                                  pastMembershipsOnly.map((membership, index) => {
                                    const startDate = new Date(membership.startDate);
                                    const endDate = new Date(membership.endDate);
                                    const now = new Date();
                                    const isExpired = endDate < now;
                                    
                                    return (
                                      <tr 
                                        key={membership.membersMembershipId}
                                        style={{ 
                                          backgroundColor: 'transparent'
                                        }}
                                      >
                                        <td>
                                          {membership.membershipPlan?.planName}
                                        </td>
                                        <td>{startDate.toLocaleDateString()}</td>
                                        <td>{endDate.toLocaleDateString()}</td>
                                        <td>{membership.membershipPlan?.durationInMonths} months</td>
                                        <td>₹{membership.totalAmount.toFixed(2)}</td>
                                        <td style={{ color: '#28a745', fontWeight: '600' }}>₹{membership.paidAmount.toFixed(2)}</td>
                                        <td style={{ color: membership.remainingAmount > 0 ? '#dc3545' : '#6B7280', fontWeight: '600' }}>
                                          ₹{membership.remainingAmount.toFixed(2)}
                                        </td>
                                        <td>
                                          {membership.isInactive ? (
                                            <span style={{ 
                                              padding: '0.25rem 0.75rem',
                                              backgroundColor: '#FEE2E2',
                                              color: '#EF4444',
                                              borderRadius: '9999px',
                                              fontSize: '0.75rem',
                                              fontWeight: '600'
                                            }}>
                                              Inactive
                                            </span>
                                          ) : membership.isActive ? (
                                            <span style={{ 
                                              padding: '0.25rem 0.75rem',
                                              backgroundColor: '#D1FAE5',
                                              color: '#10B981',
                                              borderRadius: '9999px',
                                              fontSize: '0.75rem',
                                              fontWeight: '600'
                                            }}>
                                              Active
                                            </span>
                                          ) : isExpired ? (
                                            <span style={{ 
                                              padding: '0.25rem 0.75rem',
                                              backgroundColor: '#F3F4F6',
                                              color: '#6B7280',
                                              borderRadius: '9999px',
                                              fontSize: '0.75rem',
                                              fontWeight: '600'
                                            }}>
                                              Expired
                                            </span>
                                          ) : (
                                            <span style={{ 
                                              padding: '0.25rem 0.75rem',
                                              backgroundColor: '#FEF3C7',
                                              color: '#F59E0B',
                                              borderRadius: '9999px',
                                              fontSize: '0.75rem',
                                              fontWeight: '600'
                                            }}>
                                              Pending
                                            </span>
                                          )}
                                        </td>
                                        <td style={{ fontSize: '0.875rem' }}>{membership.createdBy || 'N/A'}</td>
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </table>
                          </div>
                        );
                      })()}
                      
                      <div style={{ 
                        marginTop: '1.5rem', 
                        padding: '1rem', 
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px'
                      }}>
                        <h4 style={{ marginTop: 0, marginBottom: '0.5rem', fontSize: '1rem', color: '#374151' }}>Summary (Past Memberships Only)</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                          <div>
                            <small style={{ color: '#666', fontSize: '0.75rem' }}>Past Memberships</small>
                            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#4F46E5' }}>
                              {(() => {
                                const uniqueMemberships = pastMemberships.filter((membership, index, self) => 
                                  index === self.findIndex(m => m.membersMembershipId === membership.membersMembershipId)
                                );
                                const pastOnly = uniqueMemberships.filter(m => m.membersMembershipId !== selectedMember.membersMembershipId);
                                return pastOnly.length;
                              })()}
                            </div>
                          </div>
                          <div>
                            <small style={{ color: '#666', fontSize: '0.75rem' }}>Total Revenue</small>
                            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#28a745' }}>
                              ₹{(() => {
                                const uniqueMemberships = pastMemberships.filter((membership, index, self) => 
                                  index === self.findIndex(m => m.membersMembershipId === membership.membersMembershipId)
                                );
                                const pastOnly = uniqueMemberships.filter(m => m.membersMembershipId !== selectedMember.membersMembershipId);
                                return pastOnly.reduce((sum, m) => sum + m.totalAmount, 0).toFixed(2);
                              })()}
                            </div>
                          </div>
                          <div>
                            <small style={{ color: '#666', fontSize: '0.75rem' }}>Total Paid</small>
                            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#28a745' }}>
                              ₹{(() => {
                                const uniqueMemberships = pastMemberships.filter((membership, index, self) => 
                                  index === self.findIndex(m => m.membersMembershipId === membership.membersMembershipId)
                                );
                                const pastOnly = uniqueMemberships.filter(m => m.membersMembershipId !== selectedMember.membersMembershipId);
                                return pastOnly.reduce((sum, m) => sum + m.paidAmount, 0).toFixed(2);
                              })()}
                            </div>
                          </div>
                          <div>
                            <small style={{ color: '#666', fontSize: '0.75rem' }}>Total Pending</small>
                            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#dc3545' }}>
                              ₹{(() => {
                                const uniqueMemberships = pastMemberships.filter((membership, index, self) => 
                                  index === self.findIndex(m => m.membersMembershipId === membership.membersMembershipId)
                                );
                                const pastOnly = uniqueMemberships.filter(m => m.membersMembershipId !== selectedMember.membersMembershipId);
                                return pastOnly.reduce((sum, m) => sum + m.remainingAmount, 0).toFixed(2);
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Edit Member Details Tab */}
              {activeModalTab === 'editDetails' && (
                <>
                  <div style={{ maxWidth: '600px' }}>
                    <h3 style={{ marginTop: 0, marginBottom: '1.5rem', color: '#374151' }}>
                      Edit Member Information
                    </h3>
                    
                    <div className="form-group">
                      <label className="form-label" htmlFor="editFirstName">First Name *</label>
                      <input
                        type="text"
                        className="form-input"
                        id="editFirstName"
                        defaultValue={selectedMember?.enquiry?.firstName}
                        placeholder="Enter first name"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="editLastName">Last Name *</label>
                      <input
                        type="text"
                        className="form-input"
                        id="editLastName"
                        defaultValue={selectedMember?.enquiry?.lastName}
                        placeholder="Enter last name"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="editEmail">Email</label>
                      <input
                        type="email"
                        className="form-input"
                        id="editEmail"
                        defaultValue={selectedMember?.enquiry?.email}
                        placeholder="Enter email address"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="editPhone">Phone *</label>
                      <input
                        type="tel"
                        className="form-input"
                        id="editPhone"
                        defaultValue={selectedMember?.enquiry?.phone}
                        placeholder="Enter phone number"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="editAddress">Address</label>
                      <textarea
                        className="form-input"
                        id="editAddress"
                        rows={3}
                        defaultValue={selectedMember?.enquiry?.address}
                        placeholder="Enter address"
                        style={{ resize: 'vertical' }}
                      />
                    </div>

                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr', 
                      gap: '1rem' 
                    }}>
                      <div className="form-group">
                        <label className="form-label" htmlFor="editCity">City</label>
                        <input
                          type="text"
                          className="form-input"
                          id="editCity"
                          defaultValue={selectedMember?.enquiry?.city}
                          placeholder="City"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label" htmlFor="editGender">Gender</label>
                        <select
                          className="form-input"
                          id="editGender"
                          defaultValue={selectedMember?.enquiry?.gender || ''}
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr', 
                      gap: '1rem' 
                    }}>
                      <div className="form-group">
                        <label className="form-label" htmlFor="editDateOfBirth">Date of Birth</label>
                        <input
                          type="date"
                          className="form-input"
                          id="editDateOfBirth"
                          defaultValue={selectedMember?.enquiry?.dateOfBirth ? new Date(selectedMember.enquiry.dateOfBirth).toISOString().split('T')[0] : ''}
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label" htmlFor="editOccupation">Occupation</label>
                        <input
                          type="text"
                          className="form-input"
                          id="editOccupation"
                          defaultValue={selectedMember?.enquiry?.occupation}
                          placeholder="Occupation"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="editInterestType">Interest Type</label>
                      <select
                        className="form-input"
                        id="editInterestType"
                        defaultValue={selectedMember?.enquiry?.interestType || ''}
                      >
                        <option value="">Select Interest Type</option>
                        <option value="Weight Loss">Weight Loss</option>
                        <option value="Muscle Gain">Muscle Gain</option>
                        <option value="General Fitness">General Fitness</option>
                        <option value="Cardio">Cardio</option>
                        <option value="Strength Training">Strength Training</option>
                        <option value="CrossFit">CrossFit</option>
                        <option value="Yoga">Yoga</option>
                        <option value="Zumba">Zumba</option>
                        <option value="Body Building">Body Building</option>
                        <option value="Sports Specific">Sports Specific</option>
                        <option value="Rehabilitation">Rehabilitation</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="editRemark">Remarks / Notes</label>
                      <textarea
                        className="form-input"
                        id="editRemark"
                        rows={4}
                        defaultValue={selectedMember?.enquiry?.remark}
                        placeholder="Add any additional remarks or notes..."
                        style={{ resize: 'vertical' }}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="editIsWhatsapp" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="checkbox"
                          id="editIsWhatsapp"
                          defaultChecked={selectedMember?.enquiry?.isWhatsappNumber}
                          style={{ width: 'auto', margin: 0 }}
                        />
                        <span>Phone number is WhatsApp number</span>
                      </label>
                    </div>

                    <div style={{ 
                      display: 'flex', 
                      gap: '1rem', 
                      marginTop: '2rem',
                      paddingTop: '1.5rem',
                      borderTop: '1px solid #E5E7EB'
                    }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setActiveModalTab('receipts')}
                        style={{ flex: 1 }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={async () => {
                          const firstName = (document.getElementById('editFirstName') as HTMLInputElement).value;
                          const lastName = (document.getElementById('editLastName') as HTMLInputElement).value;
                          const email = (document.getElementById('editEmail') as HTMLInputElement).value;
                          const phone = (document.getElementById('editPhone') as HTMLInputElement).value;
                          const address = (document.getElementById('editAddress') as HTMLTextAreaElement).value;
                          const city = (document.getElementById('editCity') as HTMLInputElement).value;
                          const gender = (document.getElementById('editGender') as HTMLSelectElement).value;
                          const dateOfBirth = (document.getElementById('editDateOfBirth') as HTMLInputElement).value;
                          const occupation = (document.getElementById('editOccupation') as HTMLInputElement).value;
                          const interestType = (document.getElementById('editInterestType') as HTMLInputElement).value;
                          const remark = (document.getElementById('editRemark') as HTMLTextAreaElement).value;
                          const isWhatsappNumber = (document.getElementById('editIsWhatsapp') as HTMLInputElement).checked;

                          if (!firstName || !lastName || !phone) {
                            error('Please fill in all required fields (First Name, Last Name, Phone)');
                            return;
                          }

                          try {
                            await enquiryAPI.update(selectedMember!.enquiryId, {
                              firstName,
                              lastName,
                              email: email || '',
                              phone,
                              address: address || '',
                              city: city || '',
                              gender: gender || '',
                              dateOfBirth: dateOfBirth || '',
                              occupation: occupation || '',
                              interestType: interestType || '',
                              remark: remark || '',
                              isWhatsappNumber
                            });
                            success('✅ Member details updated successfully!');
                            setActiveModalTab('receipts');
                            fetchMembers(); // Refresh the members list
                          } catch (err: any) {
                            error(err.response?.data?.message || 'Failed to update member details');
                            console.error('Error updating member:', err);
                          }
                        }}
                        style={{ flex: 1 }}
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Start Date Modal */}
      {showEditStartDateModal && selectedMember && (
        <div className="modal-overlay" onClick={() => setShowEditStartDateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Edit Membership Start Date</h2>
              <button className="modal-close" onClick={() => setShowEditStartDateModal(false)}>×</button>
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <strong>Member:</strong> {selectedMember.enquiry?.firstName} {selectedMember.enquiry?.lastName}<br />
              <strong>Plan:</strong> {selectedMember.membershipPlan?.planName}<br />
              <strong>Current Start Date:</strong> {new Date(selectedMember.startDate).toLocaleDateString()}<br />
              <strong>Current End Date:</strong> {new Date(selectedMember.endDate).toLocaleDateString()}
            </div>
            
            <div className="form-group">
              <label htmlFor="newStartDate">New Start Date</label>
              <input
                type="date"
                id="newStartDate"
                defaultValue={new Date(selectedMember.startDate).toISOString().split('T')[0]}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>
            
            <div style={{ 
              padding: '1rem', 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffc107',
              borderRadius: '4px',
              marginBottom: '1rem',
              fontSize: '0.875rem'
            }}>
              <strong>⚠️ Note:</strong> Changing the start date will automatically update the end date based on the membership plan duration.
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setShowEditStartDateModal(false)}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={() => {
                  const newDate = (document.getElementById('newStartDate') as HTMLInputElement).value;
                  if (newDate) {
                    handleStartDateUpdate(selectedMember.membersMembershipId, newDate);
                  } else {
                    error('Please select a valid date');
                  }
                }}
              >
                Update Start Date
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="modal-overlay" onClick={() => setShowConfirmDialog(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Confirm Action</h2>
              <button className="modal-close" onClick={() => setShowConfirmDialog(false)}>×</button>
            </div>
            
            <div style={{ padding: '1.5rem' }}>
              <p style={{ margin: 0, fontSize: '1rem', lineHeight: '1.5' }}>{confirmDialogMessage}</p>
            </div>
            
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowConfirmDialog(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleConfirmAction}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default Members;
